import { Controller } from "@hotwired/stimulus"

// Derives the full website colour palette from 3 user-chosen hex colours,
// then writes all 12 computed values into hidden form fields so the backend
// persists them directly — no colour math runs on the server.
//
// Also sets DaisyUI-compatible CSS custom properties (--color-*) on preview
// containers so mini page previews update live.
export default class extends Controller {
  static targets = [
    "primaryInput", "primaryHex",
    "accentInput", "accentHex",
    "neutralInput", "neutralHex",
    "preview",
    "colorField"
  ]

  connect() { this.update() }

  update() {
    const primary = this.primaryInputTarget.value
    const accent  = this.accentInputTarget.value
    const neutral = this.neutralInputTarget.value

    // Update hex displays
    this.primaryHexTarget.textContent = primary
    this.accentHexTarget.textContent  = accent
    this.neutralHexTarget.textContent = neutral

    // Derive full palette
    const neutralDark      = this.deriveNeutral(neutral)
    const primaryContent   = this.contrastText(primary)
    const accentAdjusted   = this.ensureContrast(accent, neutral, 3.0)
    const accentContent    = this.contrastText(accentAdjusted)
    const base200          = this.darken(neutral, 0.08)
    const neutralContent   = this.contrastText(neutralDark)

    // Muted variants — blended toward background, guaranteed ≥ 4.5:1
    const primaryContentMuted = this.mutedContent(primaryContent, primary)
    const baseContentMuted    = this.mutedContent(neutralDark, neutral)
    const neutralContentMuted = this.mutedContent(neutralContent, neutralDark)

    // All 12 colours keyed by their CSS custom property name
    const colors = {
      "color-primary":              primary,
      "color-primary-content":      primaryContent,
      "color-primary-content-muted": primaryContentMuted,
      "color-accent":               accentAdjusted,
      "color-accent-content":       accentContent,
      "color-base-100":             neutral,
      "color-base-200":             base200,
      "color-base-content":         neutralDark,
      "color-base-content-muted":   baseContentMuted,
      "color-neutral":              neutralDark,
      "color-neutral-content":      neutralContent,
      "color-neutral-content-muted": neutralContentMuted
    }

    // Write computed values into hidden form fields
    this.colorFieldTargets.forEach(field => {
      const key = field.dataset.colorKey
      if (colors[key] !== undefined) field.value = colors[key]
    })

    // Set CSS custom properties on each preview container
    this.previewTargets.forEach(el => {
      for (const [prop, value] of Object.entries(colors)) {
        el.style.setProperty(`--${prop}`, value)
      }
    })
  }

  // --- oklab colour math ---

  hexToRgb(hex) {
    hex = hex.replace(/^#/, "")
    return [
      parseInt(hex.slice(0, 2), 16) / 255,
      parseInt(hex.slice(2, 4), 16) / 255,
      parseInt(hex.slice(4, 6), 16) / 255
    ]
  }

  rgbToHex([r, g, b]) {
    const clamp = v => Math.max(0, Math.min(1, v))
    const toByte = v => Math.round(clamp(v) * 255).toString(16).padStart(2, "0")
    return "#" + toByte(r) + toByte(g) + toByte(b)
  }

  srgbToLinear(c) {
    return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  }

  linearToSrgb(c) {
    return c <= 0.0031308 ? c * 12.92 : 1.055 * Math.pow(c, 1 / 2.4) - 0.055
  }

  hexToOklab(hex) {
    const [r, g, b] = this.hexToRgb(hex).map(c => this.srgbToLinear(c))

    const l_ = 0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b
    const m_ = 0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b
    const s_ = 0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b

    const lc = Math.cbrt(l_), mc = Math.cbrt(m_), sc = Math.cbrt(s_)

    return [
      0.2104542553 * lc + 0.7936177850 * mc - 0.0040720468 * sc,
      1.9779984951 * lc - 2.4285922050 * mc + 0.4505937099 * sc,
      0.0259040371 * lc + 0.7827717662 * mc - 0.8086757660 * sc
    ]
  }

  oklabToHex([L, a, b]) {
    const l_ = L + 0.3963377774 * a + 0.2158037573 * b
    const m_ = L - 0.1055613458 * a - 0.0638541728 * b
    const s_ = L - 0.0894841775 * a - 1.2914855480 * b

    const r = +4.0767416621 * (l_ ** 3) - 3.3077115913 * (m_ ** 3) + 0.2309699292 * (s_ ** 3)
    const g = -1.2684380046 * (l_ ** 3) + 2.6097574011 * (m_ ** 3) - 0.3413193965 * (s_ ** 3)
    const b2 = -0.0041960863 * (l_ ** 3) - 0.7034186147 * (m_ ** 3) + 1.7076147010 * (s_ ** 3)

    return this.rgbToHex([this.linearToSrgb(r), this.linearToSrgb(g), this.linearToSrgb(b2)])
  }

  darken(hex, amount) {
    const [L, a, b] = this.hexToOklab(hex)
    return this.oklabToHex([Math.max(0, L - amount), a, b])
  }

  deriveNeutral(hex) {
    let [, a, b] = this.hexToOklab(hex)
    const chroma = Math.sqrt(a * a + b * b)
    if (chroma > 0) {
      const scale = Math.min(chroma, 0.02) / chroma
      a *= scale
      b *= scale
    }
    const lum = this.relativeLuminance(hex)
    return this.oklabToHex([lum > 0.18 ? 0.22 : 0.95, a, b])
  }

  ensureContrast(fgHex, bgHex, minRatio) {
    if (this.contrastRatio(fgHex, bgHex) >= minRatio) return fgHex

    const [, a, b] = this.hexToOklab(fgHex)
    const bgLight = this.relativeLuminance(bgHex) > 0.18
    const [origL] = this.hexToOklab(fgHex)

    let lo = bgLight ? 0.0 : origL
    let hi = bgLight ? origL : 1.0

    for (let i = 0; i < 20; i++) {
      const mid = (lo + hi) / 2
      const candidate = this.oklabToHex([mid, a, b])
      if (this.contrastRatio(candidate, bgHex) >= minRatio) {
        bgLight ? (lo = mid) : (hi = mid)
      } else {
        bgLight ? (hi = mid) : (lo = mid)
      }
    }

    const target = bgLight ? lo : hi
    return this.oklabToHex([target, a, b])
  }

  mutedContent(contentHex, bgHex, factor = 0.45) {
    const blended = this.blend(contentHex, bgHex, factor)
    return this.ensureContrast(blended, bgHex, 4.5)
  }

  blend(hexA, hexB, factor) {
    const [la, aa, ba] = this.hexToOklab(hexA)
    const [lb, ab, bb] = this.hexToOklab(hexB)
    return this.oklabToHex([
      la + (lb - la) * factor,
      aa + (ab - aa) * factor,
      ba + (bb - ba) * factor
    ])
  }

  contrastText(hex) {
    const whiteRatio = this.contrastRatio(hex, "#ffffff")
    const blackRatio = this.contrastRatio(hex, "#1b1b1b")
    return blackRatio >= whiteRatio ? "#1b1b1b" : "#ffffff"
  }

  contrastRatio(hexA, hexB) {
    const lumA = this.relativeLuminance(hexA)
    const lumB = this.relativeLuminance(hexB)
    const lighter = Math.max(lumA, lumB)
    const darker = Math.min(lumA, lumB)
    return (lighter + 0.05) / (darker + 0.05)
  }

  relativeLuminance(hex) {
    const [r, g, b] = this.hexToRgb(hex).map(c => this.srgbToLinear(c))
    return 0.2126 * r + 0.7152 * g + 0.0722 * b
  }
}
